import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import TradingViewChart from './TradingViewChart';
import BtcHeatmapChart from './BtcHeatmapChart';
import ChartSettingsModal from './ChartSettingsModal';
import { GET_DASHBOARD_BTC_CHART } from '../graphql/queries/dashboard';
import { TimeRange } from '../graphql/queries/positions';
import { useTheme } from './ThemeProvider';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { formatCurrencyReadable, parseFloatFromString } from '../utils/formatters';

const HEATMAP_ASSETS = [
  { id: 'bitcoin', label: 'BTC', ticker: 'BTC' },
  { id: 'ethereum', label: 'ETH', ticker: 'ETH' },
  { id: 'solana', label: 'SOL', ticker: 'SOL' },
];

const DEFAULT_CHARTS = ['BINANCE:BTCUSDT', 'BINANCE:ETHUSDT', 'OTHERS.D'];

// Load saved preferences synchronously to avoid double-fetching
const loadSavedPreferences = () => {
  try {
    const saved = localStorage.getItem('dashboard_tabbed_charts');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (_) {}
  return null;
};

const TabbedCharts = ({ hasRecentActivity = true }) => {
  const { darkMode } = useTheme();
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();
  const portfolioCurrency = currentPortfolio?.currency || 'USD';

  // Initialize state from localStorage synchronously to avoid double queries
  const savedPrefs = useMemo(() => loadSavedPreferences(), []);
  const [activeTab, setActiveTab] = useState(savedPrefs?.activeTab || 'heatmap');
  const [heatmapAsset, setHeatmapAsset] = useState(savedPrefs?.heatmapAsset || 'bitcoin');
  const [chartTabs, setChartTabs] = useState(
    Array.isArray(savedPrefs?.chartTabs) ? savedPrefs.chartTabs : DEFAULT_CHARTS
  );
  const [showSettings, setShowSettings] = useState(false);
  const [interval, setInterval] = useState(savedPrefs?.interval || 'D');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Save directly without going through nested setters
    try {
      const saved = localStorage.getItem('dashboard_tabbed_charts');
      const current = saved ? JSON.parse(saved) : {};
      localStorage.setItem('dashboard_tabbed_charts', JSON.stringify({ ...current, activeTab: tab }));
    } catch (_) {}
  };

  const handleHeatmapAssetChange = (asset) => {
    setHeatmapAsset(asset);
    try {
      const saved = localStorage.getItem('dashboard_tabbed_charts');
      const current = saved ? JSON.parse(saved) : {};
      localStorage.setItem('dashboard_tabbed_charts', JSON.stringify({ ...current, heatmapAsset: asset }));
    } catch (_) {}
  };

  const handleSettingsChange = (newSettings) => {
    try {
      const saved = localStorage.getItem('dashboard_tabbed_charts');
      const current = saved ? JSON.parse(saved) : {};
      const updates = {};
      if (newSettings.selectedCharts) {
        setChartTabs(newSettings.selectedCharts);
        updates.chartTabs = newSettings.selectedCharts;
      }
      if (newSettings.range) {
        setInterval(newSettings.range);
        updates.interval = newSettings.range;
      }
      localStorage.setItem('dashboard_tabbed_charts', JSON.stringify({ ...current, ...updates }));
    } catch (_) {}
  };

  // Fetch heatmap data - refetch when portfolio currency changes so price levels and heatmap volumes are in the correct currency
  const selectedAsset = HEATMAP_ASSETS.find((a) => a.id === heatmapAsset) || HEATMAP_ASSETS[0];
  const {
    data: heatmapData,
    loading: heatmapLoading,
    error: heatmapError,
    refetch: refetchHeatmap,
  } = useQuery(GET_DASHBOARD_BTC_CHART, {
    variables: {
      portfolioId: currentPortfolioId,
      btcSlug: selectedAsset.id,
      historyRange: TimeRange.ONE_YEAR,
      heatmapDays: 365,
    },
    skip: !currentPortfolioId,
    fetchPolicy: 'cache-and-network',
  });

  const prevCurrencyRef = useRef(portfolioCurrency);
  useEffect(() => {
    if (!currentPortfolioId || !refetchHeatmap) return;
    if (prevCurrencyRef.current !== portfolioCurrency) {
      prevCurrencyRef.current = portfolioCurrency;
      refetchHeatmap();
    }
  }, [portfolioCurrency, currentPortfolioId, refetchHeatmap]);

  // Map interval for TradingView
  const tvInterval = useMemo(() => {
    if (interval === '4H') return '240';
    return interval || 'D';
  }, [interval]);

  // All tabs
  const allTabs = useMemo(() => {
    const tabs = [{ id: 'heatmap', label: '📊 Heatmap', isHeatmap: true }];
    chartTabs.forEach((symbol) => {
      const label = symbol.replace('BINANCE:', '').replace('USDT', '');
      tabs.push({ id: symbol, label, isHeatmap: false });
    });
    return tabs;
  }, [chartTabs]);

  // Set default tab based on activity - only on initial load if no saved preference
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      // Only override if no saved preference and no recent activity
      if (!savedPrefs?.activeTab && !hasRecentActivity && chartTabs.length > 0) {
        setActiveTab(chartTabs[0]);
      }
    }
  }, [initialized, hasRecentActivity, chartTabs, savedPrefs]);

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden w-full border border-gray-200 dark:border-gray-700">
      {/* Tab Bar */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-2">
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {allTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowSettings(true)}
          className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          title="Customize charts"
        >
          ⚙️
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'heatmap' ? (
          <div>
            {/* Totals (left) + Asset Selector (right) */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              {/* Buys/Sells Totals */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-400/60" />
                  <span className="text-gray-500 dark:text-gray-400">Buys</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrencyReadable(
                      (heatmapData?.transactionHeatmap || []).reduce(
                        (sum, p) => sum + parseFloatFromString(p?.buyVolume),
                        0
                      ),
                      portfolioCurrency
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400/60" />
                  <span className="text-gray-500 dark:text-gray-400">Sells</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrencyReadable(
                      (heatmapData?.transactionHeatmap || []).reduce(
                        (sum, p) => sum + parseFloatFromString(p?.sellVolume),
                        0
                      ),
                      portfolioCurrency
                    )}
                  </span>
                </div>
              </div>

              {/* Asset Selector */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Asset:</span>
                <div className="flex gap-1">
                  {HEATMAP_ASSETS.map((asset) => (
                    <button
                      key={asset.id}
                      onClick={() => handleHeatmapAssetChange(asset.id)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        heatmapAsset === asset.id
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {asset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Heatmap Chart */}
            <BtcHeatmapChart
              ticker={`${heatmapData?.tokenWithPosition?.token?.ticker || selectedAsset.ticker}/${portfolioCurrency}`}
              priceHistory={heatmapData?.tokenWithPosition?.token?.priceHistory || []}
              heatmapPoints={heatmapData?.transactionHeatmap || []}
              isLoading={heatmapLoading}
              error={heatmapError}
              currency={portfolioCurrency}
              hideFooter
            />
          </div>
        ) : (
          <div style={{ height: '420px' }}>
            <TradingViewChart
              symbol={activeTab}
              theme={darkMode ? 'dark' : 'light'}
              height="420px"
              width="100%"
              interval={tvInterval}
              hideTopToolbar={false}
              hideSideToolbar={true}
              allowSymbolChange={false}
            />
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <ChartSettingsModal
          selectedCharts={chartTabs}
          range={interval}
          onChange={handleSettingsChange}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default TabbedCharts;
